"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { CommitmentLevel, ExecutionPatternHint, GoalDriftSummary } from "@/lib/api";
import type {
  PlanDiffEntry,
  PlanDiffResult,
  PlanDiffResponse,
  PlanFallbackBlock,
  PlanDraftBlock,
  PlanTemplateBlock,
} from "@/lib/api/projection-types";
import {
  buildCarryForwardFocusHint,
  buildCarryForwardMessage,
  buildExecutionPatternNextStep,
  getCarryForwardFocusBadgeLabel,
  getExecutionPatternCommitmentRecommendation,
} from "@/lib/carryForwardSummary";
import { getCommitmentLabel, WORK_GROUP_LABEL } from "@/lib/commitmentLabels";
import {
  BLOCK_KIND_LABEL,
  LIFE_AREA_KEYS,
  LIFE_AREA_META,
  NEUTRAL_BLOCK_COLOR,
  NEUTRAL_KINDS,
  lifeAreaColor,
} from "@/lib/lifeAreas";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { useCarryForwardContext } from "@/lib/store";
import { formatTime, parseUTCishDate } from "@/lib/utils";
import BlockEditModal, {
  type BlockEditPayload,
} from "@/components/plan/BlockEditModal";

const LIFE_AREA_LABELS: Record<string, string> = Object.fromEntries(
  LIFE_AREA_KEYS.map((k) => [k, LIFE_AREA_META[k].label]),
);

function resolveDraftLifeArea(
  entry: PlanDiffEntry,
  draftBlock?: PlanDraftBlock | null
): string | null {
  return draftBlock?.life_area ?? draftBlock?.goal_category ?? entry.current.life_area ?? entry.current.goal_category ?? null;
}

function lifeAreaDotColor(
  entry: PlanDiffEntry,
  draftBlock?: PlanDraftBlock | null
): string {
  const kind = draftBlock?.kind ?? entry.current.kind;
  if (kind && NEUTRAL_KINDS.has(kind)) return NEUTRAL_BLOCK_COLOR;
  const resolvedArea = resolveDraftLifeArea(entry, draftBlock);
  return resolvedArea
    ? lifeAreaColor(resolvedArea)
    : draftBlock?.color ?? entry.current.color ?? NEUTRAL_BLOCK_COLOR;
}

// Just the colored bar — no header, no chip legend, no card. The
// outer container in the sheet already provides the heading and
// renders the per-area rows underneath, so duplicating any of that
// here only created visual noise.
function LifeAreaBar({
  breakdown,
  totalMinutes,
}: {
  breakdown: PlanDiffResponse["life_area_breakdown"];
  totalMinutes: number;
}) {
  if (!breakdown.length || totalMinutes <= 0) return null;
  return (
    <div className="flex h-3 overflow-hidden rounded-full bg-border/30">
      {breakdown.map((area) => (
        <div
          key={area.area}
          className="h-full"
          style={{
            width: `${Math.max((area.minutes / totalMinutes) * 100, 4)}%`,
            backgroundColor: area.color,
          }}
          title={`${area.label}: ${area.minutes}m`}
        />
      ))}
    </div>
  );
}

function formatRange(entry: PlanDiffEntry["current"]) {
  if (!entry.start_time || !entry.end_time) {
    return entry.lane === "later" ? "Later" : entry.lane === "fixed" ? "Fixed commitment" : "Planned";
  }
  const start = parseUTCishDate(entry.start_time);
  const end = parseUTCishDate(entry.end_time);
  return `${formatTime(start)} - ${formatTime(end)}`;
}

function toTimeInputValue(iso: string) {
  return parseUTCishDate(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function replaceTimeOnIso(iso: string, time: string) {
  const base = parseUTCishDate(iso);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes).toISOString();
}

function DiffRow({
  entry,
  draftBlock,
  onEdit,
}: {
  entry: PlanDiffEntry;
  draftBlock?: PlanDraftBlock | null;
  accentClass?: string;
  onEdit?: (entry: PlanDiffEntry) => void;
}) {
  const dotColor = lifeAreaDotColor(entry, draftBlock);
  const isEditable = Boolean(onEdit) && entry.current.kind !== "calendar";
  const kind = draftBlock?.kind ?? entry.current.kind;
  const kindLabel = kind && kind !== "task" ? BLOCK_KIND_LABEL[kind] ?? null : null;
  const timeRange = formatRange(entry.current);
  const description = draftBlock?.description
    ? sanitizeSurfaceTitle(draftBlock.description)
    : null;
  // Flat row layout — replaces the per-entry card. Wrapping each
  // change in its own border + padded card created a stack of
  // identical rectangles that visually drowned out the actual
  // change list. A border-bottom row reads as a list item without
  // adding chrome.
  return (
    <div
      data-testid={`ai-plan-diff-row-${entry.current.id}`}
      className={`flex items-start gap-3 border-b border-border/50 px-1 py-2 last:border-b-0 transition-colors ${
        isEditable ? "cursor-pointer hover:bg-bg/40" : ""
      }`}
      onClick={isEditable ? () => onEdit?.(entry) : undefined}
    >
      <span
        data-testid={`ai-plan-diff-dot-${entry.current.id}`}
        className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: dotColor }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[11px] font-medium text-text-light tabular-nums">
            {timeRange}
          </span>
          <span className="text-sm font-medium text-text">
            {sanitizeSurfaceTitle(entry.current.title)}
          </span>
          {kindLabel && (
            <span
              data-testid={`ai-plan-diff-kind-${entry.current.id}`}
              className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted"
            >
              {kindLabel}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-0.5 text-[11px] leading-relaxed text-text-light">
            {description}
          </p>
        )}
        {entry.note && (
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted">
            {entry.note}
          </p>
        )}
      </div>
    </div>
  );
}

function DiffSection({
  title,
  emptyLabel,
  entries,
  draftBlocks,
  onEdit,
}: {
  title: string;
  emptyLabel: string;
  entries: PlanDiffEntry[];
  draftBlocks: PlanDraftBlock[];
  accentClass?: string;
  onEdit?: (entry: PlanDiffEntry) => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center justify-between gap-3 pb-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">{title}</p>
        <span className="rounded-full bg-bg px-2 py-0.5 text-[10px] font-medium text-muted">
          {entries.length}
        </span>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-muted">{emptyLabel}</p>
      ) : (
        <div className="border-t border-border/50">
          {entries.map((entry) => (
            <DiffRow
              key={`${entry.operation}-${entry.current.id}-${entry.previous?.id ?? "new"}`}
              entry={entry}
              draftBlock={draftBlocks.find((block) => block.id === entry.current.id) ?? null}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  plan: PlanDiffResult;
  onApply: () => void;
  onEditManually: () => void;
  onUpdateBlock: (blockId: string, updates: Partial<PlanDraftBlock>) => void;
  onEditExistingBlock?: (blockId: string) => void;
  onRetry: () => void;
  onSaveAsLater: () => void;
  onApplyStarterBlocks?: (blocks: PlanTemplateBlock[]) => void;
  onDismiss: () => void;
  applying: boolean;
  fallbackMutating: boolean;
  commitmentLevel: CommitmentLevel;
  onCommitmentChange: (value: CommitmentLevel) => void;
  executionPatternHintFallback?: ExecutionPatternHint | null;
  goalDriftSummaryFallback?: GoalDriftSummary | null;
}

export default function AIPlanDiffSheet({
  plan,
  onApply,
  onEditManually,
  onUpdateBlock,
  onEditExistingBlock,
  onRetry,
  onSaveAsLater,
  onApplyStarterBlocks,
  onDismiss,
  applying,
  fallbackMutating,
  commitmentLevel,
  onCommitmentChange,
  executionPatternHintFallback = null,
  goalDriftSummaryFallback = null,
}: Props) {
  const [showContextCues, setShowContextCues] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingStart, setEditingStart] = useState("");
  const [editingEnd, setEditingEnd] = useState("");
  const [editingLifeArea, setEditingLifeArea] = useState<string>("personal");
  const [editingCommitment, setEditingCommitment] = useState<CommitmentLevel>("planned");
  const [editingStarterIndex, setEditingStarterIndex] = useState<number | null>(null);
  const isFailure = plan.status === "failed";
  const [localStarterBlocks, setLocalStarterBlocks] = useState<PlanTemplateBlock[]>(
    plan.status === "failed" && plan.template_blocks ? plan.template_blocks : []
  );
  useEffect(() => {
    if (plan.status === "failed" && plan.template_blocks) {
      setLocalStarterBlocks(plan.template_blocks);
    } else {
      setLocalStarterBlocks([]);
    }
  }, [plan]);
  const carryForwardSummary = useCarryForwardContext((state) => state.summary);
  const plannedCarryForwardFocus = useCarryForwardContext((state) => state.plannedFocus);
  const readyPlan = isFailure ? null : plan;
  const taskBlocks = readyPlan ? readyPlan.blocks.filter((block) => block.kind === "task") : [];
  const fixedBlocks = readyPlan ? readyPlan.blocks.filter((block) => block.kind === "calendar") : [];
  const laterBlocks = readyPlan
    ? readyPlan.blocks.filter((block) => block.kind === "custom" || block.commitment_level === "flexible")
    : [];
  const recoveryBlocks = readyPlan ? readyPlan.blocks.filter((block) => block.kind === "break" || block.kind === "buffer") : [];
  const plannedFocusForPlanDate =
    plannedCarryForwardFocus?.date === plan.date ? plannedCarryForwardFocus : null;
  const displayCarryForwardSummary = plannedFocusForPlanDate ? null : carryForwardSummary;
  const carryForwardMessage =
    displayCarryForwardSummary && displayCarryForwardSummary.total > 0
      ? buildCarryForwardMessage(displayCarryForwardSummary)
      : null;
  const carryForwardFocusHint = displayCarryForwardSummary
    ? buildCarryForwardFocusHint(displayCarryForwardSummary)
    : null;
  const executionPatternHint =
    carryForwardSummary?.execution_pattern_hint ??
    plannedFocusForPlanDate?.executionPatternHint ??
    executionPatternHintFallback ??
    null;
  const executionPatternAction = buildExecutionPatternNextStep(executionPatternHint);
  const commitmentRecommendation = getExecutionPatternCommitmentRecommendation(executionPatternHint);
  const goalDriftSummary = goalDriftSummaryFallback;
  const goalDriftPrimaryGoal = goalDriftSummary?.primary_goal ?? null;
  const goalDriftPrimaryGoalPct = goalDriftPrimaryGoal
    ? Math.round(goalDriftPrimaryGoal.progress_pct * 100)
    : 0;
  const carryForwardPanelTitle = carryForwardMessage
    ? "Carry-forward focus"
    : plannedFocusForPlanDate
      ? "Today's start block"
      : null;
  const carryForwardPanelBody =
    carryForwardMessage ||
    (plannedFocusForPlanDate
      ? `Your earlier plan already picked ${sanitizeSurfaceTitle(plannedFocusForPlanDate.title)} as today's restart block.`
      : null);
  const carryForwardPanelHint =
    (plannedFocusForPlanDate
      ? `Start with ${sanitizeSurfaceTitle(plannedFocusForPlanDate.title)}.`
      : null) ||
    carryForwardFocusHint;
  const hiddenContextCueCount =
    (executionPatternHint ? 1 : 0) +
    (goalDriftSummary ? 1 : 0);

  const formatFallbackRange = (block: PlanFallbackBlock) => {
    const start = parseUTCishDate(block.start_time);
    const end = parseUTCishDate(block.end_time);
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const beginEditEntry = useCallback(
    (entry: PlanDiffEntry) => {
      if (plan.status !== "ready") return;
      const block = plan.blocks.find((candidate) => candidate.id === entry.current.id);
      if (!block) {
        onEditExistingBlock?.(entry.current.id);
        return;
      }
      if (block.kind === "calendar") {
        onEditExistingBlock?.(entry.current.id);
        return;
      }
      setEditingBlockId(block.id);
      setEditingTitle(block.title);
      setEditingDescription(block.description ?? "");
      setEditingStart(toTimeInputValue(block.start_time));
      setEditingEnd(toTimeInputValue(block.end_time));
      setEditingLifeArea(block.life_area ?? block.goal_category ?? "personal");
      setEditingCommitment((block.commitment_level as CommitmentLevel) ?? "planned");
    },
    [onEditExistingBlock, plan]
  );

  const closeEditingBlock = useCallback(() => {
    setEditingBlockId(null);
    setEditingStarterIndex(null);
  }, []);

  // codex follow-up 2026-05-25: the OLD inline edit-draft-block
  // modal didn't match the polished BlockEditModal used on the
  // Plan timeline, and its Life-area selection wasn't always
  // wired through to the saved block. Both saveEditedBlock and
  // saveEditedStarterBlock now go through ONE BlockEditPayload
  // handler shared with the BlockEditModal component so the UI
  // and persistence semantics are identical between drafts and
  // committed blocks.
  const handleSaveDraftBlock = useCallback(
    async (payload: BlockEditPayload) => {
      const apply = (block: { start_time: string; end_time: string }) => {
        const nextStart = replaceTimeOnIso(block.start_time, payload.startTime);
        let nextEnd = replaceTimeOnIso(block.end_time, payload.endTime);
        if (new Date(nextEnd).getTime() <= new Date(nextStart).getTime()) {
          nextEnd = new Date(
            new Date(nextStart).getTime() + 30 * 60_000,
          ).toISOString();
        }
        return { nextStart, nextEnd };
      };

      if (editingStarterIndex !== null) {
        setLocalStarterBlocks((prev) =>
          prev.map((block, idx) => {
            if (idx !== editingStarterIndex) return block;
            const { nextStart, nextEnd } = apply(block);
            return {
              ...block,
              title: payload.title.trim() || block.title,
              description: payload.description.trim() || block.description,
              start_time: nextStart,
              end_time: nextEnd,
              life_area: payload.lifeArea ?? block.life_area,
              commitment_level: payload.commitmentLevel,
            };
          }),
        );
        setEditingStarterIndex(null);
        return;
      }

      if (plan.status !== "ready" || !editingBlockId) return;
      const block = plan.blocks.find(
        (candidate) => candidate.id === editingBlockId,
      );
      if (!block) return;
      const { nextStart, nextEnd } = apply(block);
      onUpdateBlock(editingBlockId, {
        title: payload.title.trim() || block.title,
        description: payload.description.trim() || null,
        start_time: nextStart,
        end_time: nextEnd,
        // Explicit life_area + goal_category mirror so the breakdown
        // counts this block under the chosen area (the old modal had
        // a wiring gap here for some draft paths).
        life_area: payload.lifeArea ?? null,
        goal_category: payload.lifeArea ?? null,
        commitment_level: payload.commitmentLevel,
      });
      setEditingBlockId(null);
    },
    [editingBlockId, editingStarterIndex, onUpdateBlock, plan],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // h-screen + flex justify-end gives the sheet a defined
      // viewport height to inherit from. Some browsers (Safari iOS
      // mostly) don't compute height correctly when only `inset-0`
      // is set on a `fixed` flex parent, so the inner h-full
      // collapses and the footer renders off-screen.
      className="pointer-events-none fixed inset-0 z-50 flex h-screen justify-end"
    >
      <div className="pointer-events-auto absolute inset-0 bg-black/40 lg:left-60" onClick={onDismiss} />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        // Explicit height ladder: h-screen → flex flex-col → child
        // header is shrink-0, content is flex-1 min-h-0 (so it can
        // shrink and scroll), footer is shrink-0 (so it always
        // takes its natural height even when content is huge).
        // Without min-h-0 on the scroll child, flexbox lets it grow
        // to its content height and pushes the footer past the
        // viewport.
        style={{ maxHeight: "100dvh" }}
        className="pointer-events-auto relative z-10 flex h-screen w-full max-w-3xl flex-col bg-cream shadow-2xl"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="text-base font-bold text-text" data-testid="ai-plan-heading">
              AI Time Box
            </h2>
            <p className="text-[10px] text-muted">{plan.date}</p>
          </div>
          <button
            onClick={onDismiss}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-border/30 text-muted hover:text-text"
            aria-label="Close AI Time Box"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-5">
          {isFailure ? (
            <>
              <div className="rounded-xl border border-border bg-card p-4" data-testid="ai-plan-failure-card">
                <p className="text-sm leading-relaxed text-text" data-testid="ai-plan-failure-message">
                  {plan.error_message}
                </p>
              </div>

              {localStarterBlocks.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted mb-3">
                    Starter blocks
                  </p>
                  <p className="text-[11px] text-text-light mb-4">
                    Click any block to edit before applying. Remove what doesn&apos;t fit.
                  </p>
                  <div className="space-y-2">
                    {localStarterBlocks.map((block, i) => {
                      const dotColor = lifeAreaColor(block.life_area);
                      const startTime = parseUTCishDate(block.start_time);
                      const endTime = parseUTCishDate(block.end_time);
                      const dur = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
                      return (
                        <div
                          key={i}
                          className="group flex items-center gap-3 rounded-xl border border-border bg-white px-3 py-3"
                        >
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: dotColor }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setEditingStarterIndex(i);
                              setEditingTitle(block.title);
                              setEditingDescription(block.description ?? "");
                              setEditingStart(toTimeInputValue(block.start_time));
                              setEditingEnd(toTimeInputValue(block.end_time));
                              setEditingLifeArea(block.life_area ?? "personal");
                              setEditingCommitment(
                                (block.commitment_level as CommitmentLevel) ?? "planned"
                              );
                            }}
                            data-testid={`ai-plan-starter-block-${i}`}
                            className="flex min-w-0 flex-1 flex-col items-start text-left transition-colors hover:text-forest"
                          >
                            <p className="text-sm font-medium text-text">{block.title}</p>
                            <p className="text-[11px] text-text-light">
                              {formatTime(startTime)} – {formatTime(endTime)} · {dur}m
                            </p>
                            <p className="mt-0.5 text-[10px] text-muted">{block.description}</p>
                          </button>
                          {block.life_area && (
                            <span className="shrink-0 rounded-full bg-bg px-2 py-0.5 text-[10px] font-medium capitalize text-muted">
                              {block.life_area}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setLocalStarterBlocks((prev) => prev.filter((_, idx) => idx !== i))
                            }
                            aria-label={`Remove ${block.title}`}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-secondary/60 hover:text-rose"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {plan.preserved_context.source_title && (
                <div className="rounded-xl border border-blue/15 bg-blue/5 p-4" data-testid="ai-plan-failure-context">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue">
                    Your context
                  </p>
                  <p className="mt-2 text-sm font-semibold text-text">
                    {sanitizeSurfaceTitle(plan.preserved_context.source_title)}
                  </p>
                  {plan.preserved_context.note && (
                    <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                      {sanitizeSurfaceTitle(plan.preserved_context.note)}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div className="space-y-4">
                  {/* Compact draft summary */}
                  <div className="rounded-xl border border-border bg-card px-4 py-3">
                    <p className="text-xs leading-relaxed text-text" data-testid="ai-plan-summary">
                      {sanitizeSurfaceTitle(plan.summary)}
                    </p>
                    <p className="mt-2 text-[10px] text-muted">
                      Click any added, moved, or preserved draft block below to edit its title, description, time, or life area before applying.
                    </p>
                  </div>

                  {carryForwardPanelTitle && carryForwardPanelBody && (
                    <div
                      data-testid="ai-plan-carry-forward-context"
                      className="mt-4 rounded-xl border border-sage/45 bg-sage-pale/55 p-3"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-forest-med">
                        {carryForwardPanelTitle}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                        {sanitizeSurfaceTitle(carryForwardPanelBody)}
                      </p>
                      {carryForwardPanelHint && (
                        <p className="mt-2 text-[11px] font-medium leading-relaxed text-forest-med">
                          {sanitizeSurfaceTitle(carryForwardPanelHint)}
                        </p>
                      )}
                    </div>
                  )}

                  {hiddenContextCueCount > 0 && (
                    <div className="mt-4 rounded-xl border border-border/80 bg-bg/70 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                            Supporting cues
                          </p>
                          <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                            {showContextCues
                              ? "Deeper pattern and goal-trajectory cues are open."
                              : `${hiddenContextCueCount} deeper cue${hiddenContextCueCount === 1 ? "" : "s"} are tucked away until you need them.`}
                          </p>
                        </div>
                        <button
                          type="button"
                          data-testid="ai-plan-context-toggle"
                          onClick={() => setShowContextCues((current) => !current)}
                          className="shrink-0 rounded-full border border-border bg-white px-3 py-1.5 text-[11px] font-semibold text-text-light transition hover:bg-bg"
                        >
                          {showContextCues
                            ? "Hide context cues"
                            : `Show ${hiddenContextCueCount} cue${hiddenContextCueCount === 1 ? "" : "s"}`}
                        </button>
                      </div>
                    </div>
                  )}

                  {showContextCues && executionPatternHint && (
                    <div
                      data-testid="ai-plan-execution-pattern-hint"
                      className="mt-4 rounded-xl border border-forest/15 bg-white/70 p-3"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-forest-med">
                        Execution pattern
                      </p>
                      <p className="mt-1 text-[11px] font-semibold text-text">{executionPatternHint.title}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                        {executionPatternHint.message}
                      </p>
                      {executionPatternAction && (
                        <p className="mt-2 text-[11px] font-medium leading-relaxed text-forest-med">
                          Best next move: {executionPatternAction}
                        </p>
                      )}
                    </div>
                  )}

                  {showContextCues && goalDriftSummary && (
                    <div
                      data-testid="ai-plan-goal-drift-context"
                      className="mt-4 rounded-xl border border-blue/15 bg-blue/5 p-3"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue">
                        Goal trajectory
                      </p>
                      <p className="mt-1 text-[11px] font-semibold text-text">
                        {sanitizeSurfaceTitle(goalDriftSummary.headline)}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                        {sanitizeSurfaceTitle(goalDriftSummary.message)}
                      </p>
                      {goalDriftSummary.next_step && (
                        <p className="mt-2 text-[11px] font-medium leading-relaxed text-forest-med">
                          Best next move: {sanitizeSurfaceTitle(goalDriftSummary.next_step)}
                        </p>
                      )}
                      {/* Render every goal the user queued via
                          "Planning this" (top_drifting_goals carries
                          them all from the projection). Falls back
                          to primary when the array is empty so the
                          card still shows something useful. */}
                      {(() => {
                        const ranked =
                          (goalDriftSummary.top_drifting_goals?.length ?? 0) > 0
                            ? goalDriftSummary.top_drifting_goals ?? []
                            : goalDriftPrimaryGoal
                              ? [goalDriftPrimaryGoal]
                              : [];
                        if (ranked.length === 0) return null;
                        return (
                          <div className="mt-3 space-y-2">
                            {ranked.map((goal) => {
                              const pct = Math.round((goal.progress_pct ?? 0) * 100);
                              return (
                                <div
                                  key={goal.id}
                                  className="rounded-lg border border-white/80 bg-white/80 px-3 py-2"
                                  data-testid={`ai-plan-goal-trajectory-${goal.id}`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-semibold text-text">
                                        {sanitizeSurfaceTitle(goal.title)}
                                      </p>
                                      <p className="mt-0.5 text-[10px] text-muted">
                                        {goal.open_task_count} open · {goal.recent_rollover_count} recent restart
                                        {goal.recent_rollover_count === 1 ? "" : "s"}
                                      </p>
                                    </div>
                                    <span className="rounded-full bg-sage-pale px-2 py-0.5 text-[10px] font-semibold text-forest-med">
                                      {pct}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Commitment toggle removed — blocks default to "planned" */}
                </div>

                <div
                  className="rounded-xl border border-border bg-card p-4"
                  data-testid="ai-plan-life-area-breakdown"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted mb-3">
                    Time by life area
                  </p>
                  <LifeAreaBar
                    breakdown={plan.life_area_breakdown}
                    totalMinutes={plan.total_focus_minutes + plan.total_break_minutes}
                  />
                  <div className="mt-3 space-y-1.5">
                    {plan.life_area_breakdown.map((entry) => {
                      const dotColor = lifeAreaColor(entry.area);
                      return (
                        <div
                          key={entry.area}
                          className="flex items-center justify-between"
                          data-testid={`ai-plan-life-area-${entry.area}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
                            <span className="text-xs text-text">{entry.label}</span>
                          </div>
                          <span className="text-xs font-medium text-text-light">{entry.minutes}m</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <DiffSection
                title="Adds"
                emptyLabel="Nothing new needs to be added for this draft."
                entries={plan.adds}
                draftBlocks={plan.blocks}
                onEdit={beginEditEntry}
              />
              <DiffSection
                title="Moves"
                emptyLabel="Nothing has to move to make this draft work."
                entries={plan.moves}
                draftBlocks={plan.blocks}
                onEdit={beginEditEntry}
              />
              <DiffSection
                title="Leaves untouched"
                emptyLabel="This draft is mostly additive, so there is not much to preserve."
                entries={plan.untouched}
                draftBlocks={plan.blocks}
                onEdit={beginEditEntry}
              />
            </>
          )}
        </div>

        <div
          // The BottomNav (fixed bottom-0 z-50) was occluding the Apply
          // button on mobile — both live in the same z-layer and
          // overlap inside the viewport. On <lg viewports we add bottom
          // padding equal to the nav height (~72px) plus the iOS
          // safe-area-inset, so the buttons sit cleanly above the nav.
          // Desktop keeps the original p-4 because the sidebar nav
          // doesn't occupy the bottom edge.
          className="border-t border-border bg-cream/95 backdrop-blur p-4 pb-[calc(env(safe-area-inset-bottom)+72px)] lg:pb-4 shrink-0"
          data-testid="ai-plan-footer"
        >
          {/* UAT 2026-05-25 bug #2 — user reported "i later foundout i
              have to click on that apply button". The preview-vs-commit
              boundary wasn't visible. Add a one-line hint above the
              buttons so the user understands this is the commit step. */}
          {!isFailure && (
            <p className="mb-2 text-center text-[11.5px] leading-relaxed text-text-light sm:text-left">
              Nothing is saved yet. Click <span className="font-semibold text-forest-med">Save to my day</span> to apply these blocks to Plan.
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              onClick={onDismiss}
              data-testid="ai-plan-cancel-button"
              className="rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-forest/20 hover:text-text sm:flex-1"
            >
              {isFailure ? "Close" : "Cancel"}
            </button>
            {isFailure ? (
              <button
                onClick={() => {
                  if (onApplyStarterBlocks && localStarterBlocks.length > 0) {
                    onApplyStarterBlocks(localStarterBlocks);
                  } else {
                    onSaveAsLater();
                  }
                }}
                disabled={
                  applying ||
                  fallbackMutating ||
                  (Boolean(onApplyStarterBlocks) && localStarterBlocks.length === 0)
                }
                data-testid="ai-plan-apply-starter-blocks"
                className="rounded-xl bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-50 sm:flex-1"
              >
                {fallbackMutating ? "Saving…" : "Save to my day"}
              </button>
            ) : (
              <>
                <button
                  onClick={onApply}
                  disabled={applying}
                  data-testid="ai-plan-apply-button"
                  className="rounded-xl bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-50 sm:flex-1"
                >
                  {applying ? "Saving…" : "Save to my day"}
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* codex follow-up 2026-05-25: draft-block edit modal now
          reuses the polished BlockEditModal component (same one used
          on the persisted Plan timeline). User screenshot showed the
          old inline modal had inconsistent styling vs the timeline's
          edit modal AND a wiring gap where Life-area selection
          didn't always reach the saved block. handleSaveDraftBlock
          (defined above) unifies both the saveEditedBlock and
          saveEditedStarterBlock paths into a single BlockEditPayload
          consumer. */}
      <div
        className="pointer-events-auto absolute inset-0 z-20"
        data-testid="ai-plan-edit-modal-wrapper"
        style={{
          pointerEvents:
            ((editingBlockId && !isFailure) || editingStarterIndex !== null)
              ? "auto"
              : "none",
        }}
      >
        <BlockEditModal
          open={
            Boolean((editingBlockId && !isFailure) || editingStarterIndex !== null)
          }
          initial={{
            title: editingTitle,
            description: editingDescription,
            startTime: editingStart,
            endTime: editingEnd,
            commitmentLevel: editingCommitment,
            lifeArea: editingLifeArea,
            durationDays: 1,
            blockId: editingBlockId,
          }}
          onClose={closeEditingBlock}
          onSave={handleSaveDraftBlock}
          testId="ai-plan-edit-modal"
        />
      </div>
    </motion.div>
  );
}
